import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  contractsRepo,
  contractSalesRepo,
  countriesRepo,
  incotermsRepo,
  partnersRepo,
  paymentTermsRepo,
  productsRepo,
  shipperProfilesRepo,
  dealsRepo,
  dealLineItemsRepo,
  isSupabaseConfigured,
  type NewDeal,
  type NewDealLineItem,
} from "@/lib/db";
import { evaluate } from "@/lib/rules";
import { applicableTier } from "@/lib/margin/model";
import type {
  ContractRow,
  Country,
  Deal,
  DealLineItem,
  Incoterm,
  Partner,
  PaymentTerm,
  Product,
  RuleFinding,
} from "@/types";
import { ConfigBanner } from "@/components/ConfigBanner";
import { PricingPanel } from "@/components/pricing/PricingPanel";
import { Field } from "./MasterDataPage";
import { buildRuleContext } from "./buildContext";

interface LineDraft {
  product_id: string;
  quantity: string;
  unit_price: string;
  net_weight_kg: string;
  gross_weight_kg: string;
}

const EMPTY_LINE: LineDraft = {
  product_id: "",
  quantity: "1",
  unit_price: "",
  net_weight_kg: "",
  gross_weight_kg: "",
};

function numOrNull(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Entrance ② — 새 딜 만들기.
 * Target country, shipper, partner roles, incoterm + named place, transport mode,
 * payment term, and line items. Live incoterm guidance via evaluate() on
 * on_deal_save. Save → deals + deal_line_items insert → navigate to workspace.
 */
export function NewDealPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // v3 (unified-trade-flow FR-9): ?contract={id} — 계약에서 딜 생성 시 프리필.
  const contractId = searchParams.get("contract");
  const [contract, setContract] = useState<ContractRow | null>(null);
  const [contractSold, setContractSold] = useState(0);

  // master data
  const [countries, setCountries] = useState<Country[]>([]);
  const [shippers, setShippers] = useState<{ id: string; company_name: string }[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [incoterms, setIncoterms] = useState<Incoterm[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);

  // form
  const [dealNo, setDealNo] = useState("");
  const [title, setTitle] = useState("");
  const [country, setCountry] = useState("US");
  const [shipperId, setShipperId] = useState("");
  const [buyerId, setBuyerId] = useState("");
  const [consigneeId, setConsigneeId] = useState("");
  const [iorId, setIorId] = useState("");
  const [fdaImporterId, setFdaImporterId] = useState("");
  const [importerStructure, setImporterStructure] = useState("");
  const [incotermId, setIncotermId] = useState("");
  const [namedPlace, setNamedPlace] = useState("");
  const [transportMode, setTransportMode] = useState("air");
  const [paymentTermId, setPaymentTermId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [tariffAck, setTariffAck] = useState(false);
  const [paymentReceived, setPaymentReceived] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([{ ...EMPTY_LINE }]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    Promise.all([
      countriesRepo.list(),
      shipperProfilesRepo.list(),
      partnersRepo.list(),
      productsRepo.list(),
      incotermsRepo.list(),
      paymentTermsRepo.list(),
    ])
      .then(([c, s, p, pr, ic, pt]) => {
        setCountries(c);
        setShippers(s.map((x) => ({ id: x.id, company_name: x.company_name })));
        setPartners(p);
        setProducts(pr);
        setIncoterms(ic);
        setPaymentTerms(pt);
        if (s[0]) setShipperId(s[0].id);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  // 계약 프리필 — 국가·바이어·통화 승계 + 누적 확정 수량 로드 (FR-9).
  useEffect(() => {
    if (!isSupabaseConfigured() || !contractId) return;
    Promise.all([
      contractsRepo.get(contractId),
      contractSalesRepo.listByContract(contractId),
    ])
      .then(([ct, ss]) => {
        if (!ct) return;
        setContract(ct);
        setContractSold(ss.reduce((s, r) => s + (r.qty || 0), 0));
        setCountry(ct.destination_country);
        if (ct.buyer_id) {
          setBuyerId(ct.buyer_id);
          setConsigneeId((prev) => prev || ct.buyer_id!);
        }
        if (ct.currency) setCurrency(ct.currency);
        setTitle((prev) => prev || `${ct.name} 선적`);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [contractId]);

  const productById = useMemo(
    () => new Map(products.map((p) => [p.id, p])),
    [products],
  );

  /** 계약 MOQ 구간 단가 제안 — 누적 확정 + 이번 주문 수량이 걸리는 구간 (FR-9). */
  const suggestedPrice = useMemo(() => {
    if (!contract || !Array.isArray(contract.tiers) || contract.tiers.length === 0) return null;
    const orderQty = lines.reduce((s, l) => s + (numOrNull(l.quantity) ?? 0), 0);
    const { tier } = applicableTier(contract.tiers, contractSold + Math.max(orderQty, 1));
    return tier?.price ?? null;
  }, [contract, contractSold, lines]);

  const applySuggestedPrice = () => {
    if (suggestedPrice == null) return;
    setLines((ls) => ls.map((l) => (l.product_id ? { ...l, unit_price: String(suggestedPrice) } : l)));
  };

  // 표준 가격 패널 — 라인 합계 대수 + 블렌디드 단가 적용 (pricing-corridor M1).
  const orderQty = useMemo(
    () => lines.reduce((s, l) => s + (numOrNull(l.quantity) ?? 0), 0),
    [lines],
  );
  const applyBlendedToLine = (price: number) => {
    setLines((ls) => {
      const hasProduct = ls.some((l) => l.product_id);
      return ls.map((l, i) => {
        const target = hasProduct ? Boolean(l.product_id) : i === 0;
        return target ? { ...l, unit_price: String(price) } : l;
      });
    });
  };

  function setLine(i: number, patch: Partial<LineDraft>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function pickProduct(i: number, productId: string) {
    const p = productById.get(productId);
    setLine(i, {
      product_id: productId,
      // 계약 연결 시 MOQ 구간 단가가 제품 기본단가보다 우선 (수정 가능한 제안값).
      unit_price:
        suggestedPrice != null
          ? String(suggestedPrice)
          : p?.unit_price != null
            ? String(p.unit_price)
            : "",
      net_weight_kg: p?.net_weight_kg != null ? String(p.net_weight_kg) : "",
      gross_weight_kg:
        p?.gross_weight_kg != null ? String(p.gross_weight_kg) : "",
    });
  }

  // --- live preview findings (on_deal_save) ---------------------------------
  const previewFindings: RuleFinding[] = useMemo(() => {
    const incoterm = incoterms.find((i) => i.id === incotermId) ?? null;
    const countryRow = countries.find((c) => c.id === country) ?? null;
    const draftDeal: Deal = {
      id: "draft",
      deal_no: dealNo || "DRAFT",
      title: title || null,
      status: "quote",
      destination_country: country,
      shipper_profile_id: shipperId || "",
      buyer_id: buyerId || null,
      consignee_id: consigneeId || null,
      notify_id: null,
      importer_of_record_id: iorId || null,
      fda_initial_importer_id: fdaImporterId || null,
      importer_structure: (importerStructure || null) as Deal["importer_structure"],
      incoterm_id: (incotermId || null) as Deal["incoterm_id"],
      incoterm_named_place: namedPlace || null,
      incoterm_named_place_type:
        transportMode === "air" ? "airport" : transportMode === "sea" ? "port" : null,
      incoterm_version: "2020",
      transport_mode: transportMode as Deal["transport_mode"],
      cargo_type: "finished_goods",
      payment_term_id: paymentTermId || null,
      currency,
      claim_preferential: false,
      insurance_obligation_party: "none",
      insured_amount: null,
      export_declaration_status: "none",
      fob_value_krw: null,
      payment_received: paymentReceived,
      intended_use_text: null,
      tariff_ack: tariffAck,
      export_declaration_no: null,
      transport_doc_no: null,
      insurance_policy_no: null,
      lc_expiry_date: null,
      lc_latest_shipment_date: null,
      contract_id: contract?.id ?? null,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const draftLines: DealLineItem[] = lines
      .filter((l) => l.product_id)
      .map((l, i) => {
        const qty = numOrNull(l.quantity) ?? 0;
        const price = numOrNull(l.unit_price) ?? 0;
        return {
          id: `draft-${i}`,
          deal_id: "draft",
          product_id: l.product_id,
          item_kind: "product",
          line_no: i + 1,
          description_en: productById.get(l.product_id)?.description_en ?? null,
          hs_code: null,
          quantity: qty,
          unit: "units",
          unit_price: price,
          line_amount: Math.round(qty * price * 100) / 100,
          net_weight_kg: numOrNull(l.net_weight_kg),
          gross_weight_kg: numOrNull(l.gross_weight_kg),
          weight_unit: "kg",
          package_count: null,
          package_type: "carton",
          cbm: null,
          is_no_commercial_value: false,
          created_at: new Date().toISOString(),
        };
      });
    const ctx = buildRuleContext({
      deal: draftDeal,
      lines: draftLines,
      country: countryRow,
      incoterm,
      partners,
      products,
    });
    return evaluate(ctx, "on_deal_save");
  }, [
    incoterms, countries, country, dealNo, title, shipperId, buyerId,
    consigneeId, iorId, fdaImporterId, importerStructure, incotermId,
    namedPlace, transportMode, paymentTermId, currency, paymentReceived,
    tariffAck, lines, partners, products, productById,
  ]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (!shipperId) throw new Error("자사(shipper)를 선택하세요.");
      if (!country) throw new Error("목적국을 선택하세요.");
      const dealRow: NewDeal = {
        deal_no: dealNo || `BM-${Date.now()}`,
        title: title || null,
        status: "quote",
        destination_country: country,
        shipper_profile_id: shipperId,
        buyer_id: buyerId || null,
        consignee_id: consigneeId || null,
        notify_id: null,
        importer_of_record_id: iorId || null,
        fda_initial_importer_id: fdaImporterId || null,
        importer_structure: (importerStructure || null) as Deal["importer_structure"],
        incoterm_id: (incotermId || null) as Deal["incoterm_id"],
        incoterm_named_place: namedPlace || null,
        incoterm_named_place_type:
          transportMode === "air" ? "airport" : transportMode === "sea" ? "port" : null,
        incoterm_version: "2020",
        transport_mode: transportMode as Deal["transport_mode"],
        cargo_type: "finished_goods",
        payment_term_id: paymentTermId || null,
        currency,
        claim_preferential: false,
        insurance_obligation_party: "none",
        insured_amount: null,
        export_declaration_status: "none",
        fob_value_krw: null,
        payment_received: paymentReceived,
        intended_use_text: null,
        tariff_ack: tariffAck,
        export_declaration_no: null,
        transport_doc_no: null,
        insurance_policy_no: null,
        lc_expiry_date: null,
        lc_latest_shipment_date: null,
        contract_id: contract?.id ?? null,
        notes: null,
      };
      const created = await dealsRepo.create(dealRow);

      const lineRows: NewDealLineItem[] = lines
        .filter((l) => l.product_id)
        .map((l, i) => {
          const qty = numOrNull(l.quantity) ?? 0;
          const price = numOrNull(l.unit_price) ?? 0;
          const p = productById.get(l.product_id);
          return {
            deal_id: created.id,
            product_id: l.product_id,
            item_kind: "product",
            line_no: i + 1,
            description_en: p?.description_en ?? null,
            hs_code: p?.hs_code ?? null,
            quantity: qty,
            unit: "units",
            unit_price: price,
            line_amount: Math.round(qty * price * 100) / 100,
            net_weight_kg: numOrNull(l.net_weight_kg),
            gross_weight_kg: numOrNull(l.gross_weight_kg),
            weight_unit: "kg",
            package_count: null,
            package_type: "carton",
            cbm: p?.cbm ?? null,
            is_no_commercial_value: false,
          } satisfies NewDealLineItem;
        });
      await dealLineItemsRepo.createMany(lineRows);

      // 계약에서 왔으면 통합 워크데스크의 해당 계약·딜로 복귀 (FR-9).
      if (contract) navigate(`/flow?case=ct:${contract.id}&stage=3&deal=${created.id}`);
      else navigate(`/deals/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const incotermPreview = previewFindings.filter((f) => f.category === "incoterm");

  return (
    <div>
      <ConfigBanner />
      <section className="panel">
        <h2>새 딜 만들기</h2>
        <p className="sub">
          마스터데이터에서 끌어와 딜을 구성합니다. 인코텀즈 선택 시 즉시
          가이드/경고가 표시됩니다.
        </p>

        <PricingPanel
          countryCode={country}
          orderQty={orderQty}
          onApplyBlended={applyBlendedToLine}
        />

        {!isSupabaseConfigured() && (
          <p className="muted">
            Supabase 미설정 상태입니다. 환경변수를 채우면 딜을 생성할 수 있습니다.
          </p>
        )}

        {isSupabaseConfigured() && (
          <>
            <div className="form-grid">
              <Field label="Deal No.">
                <input
                  value={dealNo}
                  placeholder="BM-YYMMDD### (비우면 자동)"
                  onChange={(e) => setDealNo(e.target.value)}
                />
              </Field>
              <Field label="제목">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="TORR RF → US / ABC Medical"
                />
              </Field>
              <Field label="목적국 *">
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                >
                  <option value="">선택</option>
                  {countries.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name_ko ?? c.name_en} ({c.id})
                    </option>
                  ))}
                </select>
              </Field>
              {(() => {
                const c = countries.find((x) => x.id === country);
                if (!c) return null;
                const reqDocs = c.required_documents.filter(
                  (d) => d.requirement === "required" || d.requirement === "conditional",
                );
                return (
                  <div className="notice-info" style={{ gridColumn: "1 / -1" }}>
                    <strong>
                      {c.name_ko ?? c.name_en} 프로파일
                    </strong>
                    {c.required_importer_roles.length > 0 && (
                      <div>
                        필수 수입자 역할:{" "}
                        {c.required_importer_roles.join(", ")} — 미지정 시 발행
                        차단(딜 워크스페이스에서 지정)
                      </div>
                    )}
                    {reqDocs.length > 0 && (
                      <div>
                        필요 서류:{" "}
                        {reqDocs
                          .map((d) => d.doc_type + (d.requirement === "conditional" ? "(조건부)" : ""))
                          .join(" · ")}
                      </div>
                    )}
                    {c.regulatory_notes && <div>{c.regulatory_notes}</div>}
                  </div>
                );
              })()}
              {contract && (
                <div className="notice-info" style={{ gridColumn: "1 / -1" }}>
                  <strong>계약 연결: {contract.name}</strong>
                  <div>
                    누적 확정 실적 {contractSold}대 · 계약 MOQ 구간표{" "}
                    {contract.tiers
                      .map((t) => `${t.fromQty}${t.toQty != null ? `–${t.toQty}` : "+"}@$${t.price.toLocaleString("en-US")}`)
                      .join(" · ") || "없음"}
                  </div>
                  <div>
                    이번 주문 반영 시 적용 구간 단가 제안:{" "}
                    <strong>{suggestedPrice != null ? `$${suggestedPrice.toLocaleString("en-US")}` : "—"}</strong>{" "}
                    <button className="secondary" onClick={applySuggestedPrice} disabled={suggestedPrice == null}>
                      라인 단가에 적용
                    </button>{" "}
                    <span className="muted">(제안값 — 자유 수정 가능. 국가·바이어·통화가 계약에서 프리필됨)</span>
                  </div>
                </div>
              )}
              <Field label="자사(Shipper) *">
                <select
                  value={shipperId}
                  onChange={(e) => setShipperId(e.target.value)}
                >
                  <option value="">선택</option>
                  {shippers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.company_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Buyer">
                <PartnerSelect
                  partners={partners}
                  value={buyerId}
                  onChange={setBuyerId}
                />
              </Field>
              <Field label="Consignee">
                <PartnerSelect
                  partners={partners}
                  value={consigneeId}
                  onChange={setConsigneeId}
                />
              </Field>
              <Field label="Importer of Record (US)">
                <PartnerSelect
                  partners={partners}
                  value={iorId}
                  onChange={setIorId}
                />
              </Field>
              <Field label="FDA Initial Importer (US)">
                <PartnerSelect
                  partners={partners}
                  value={fdaImporterId}
                  onChange={setFdaImporterId}
                />
              </Field>
              <Field label="수입자 구조">
                <select
                  value={importerStructure}
                  onChange={(e) => setImporterStructure(e.target.value)}
                >
                  <option value="">선택</option>
                  <option value="distributor">distributor</option>
                  <option value="three_pl">three_pl</option>
                  <option value="us_subsidiary">us_subsidiary</option>
                  <option value="direct">direct</option>
                </select>
              </Field>
              <Field label="인코텀즈">
                <select
                  value={incotermId}
                  onChange={(e) => setIncotermId(e.target.value)}
                >
                  <option value="">선택</option>
                  {incoterms.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.id} — {i.name_ko ?? i.full_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="인코텀즈 지정장소">
                <input
                  value={namedPlace}
                  onChange={(e) => setNamedPlace(e.target.value)}
                  placeholder="ICN Airport, Incheon"
                />
              </Field>
              <Field label="운송수단">
                <select
                  value={transportMode}
                  onChange={(e) => setTransportMode(e.target.value)}
                >
                  <option value="air">air</option>
                  <option value="sea">sea</option>
                  <option value="courier">courier</option>
                  <option value="road">road</option>
                  <option value="rail">rail</option>
                  <option value="multimodal">multimodal</option>
                </select>
              </Field>
              <Field label="결제조건">
                <select
                  value={paymentTermId}
                  onChange={(e) => setPaymentTermId(e.target.value)}
                >
                  <option value="">선택</option>
                  {paymentTerms.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="통화">
                <input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                />
              </Field>
            </div>

            <div className="check-row">
              <label>
                <input
                  type="checkbox"
                  checked={tariffAck}
                  onChange={(e) => setTariffAck(e.target.checked)}
                />{" "}
                관세는 선적시점 통관사/CBP ACE에서 확인함(세율 미산출)
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={paymentReceived}
                  onChange={(e) => setPaymentReceived(e.target.checked)}
                />{" "}
                선입금 확인됨
              </label>
            </div>

            {incotermPreview.length > 0 && (
              <div className="notice-info" role="note">
                <strong>인코텀즈 가이드/경고(미리보기):</strong>
                <ul className="warn-list">
                  {incotermPreview.map((f) => (
                    <li key={f.id}>
                      <span className={`badge ${f.severity}`}>
                        {f.severity}
                      </span>{" "}
                      {f.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <h3 className="section-title">라인아이템</h3>
            <table className="findings">
              <thead>
                <tr>
                  <th>제품</th>
                  <th>수량</th>
                  <th>단가</th>
                  <th>순중량(kg)</th>
                  <th>총중량(kg)</th>
                  <th>금액</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const amount =
                    (numOrNull(l.quantity) ?? 0) * (numOrNull(l.unit_price) ?? 0);
                  return (
                    <tr key={i}>
                      <td>
                        <select
                          value={l.product_id}
                          onChange={(e) => pickProduct(i, e.target.value)}
                        >
                          <option value="">선택</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.model_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="num"
                          type="number"
                          value={l.quantity}
                          onChange={(e) =>
                            setLine(i, { quantity: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="num"
                          type="number"
                          value={l.unit_price}
                          onChange={(e) =>
                            setLine(i, { unit_price: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="num"
                          type="number"
                          value={l.net_weight_kg}
                          onChange={(e) =>
                            setLine(i, { net_weight_kg: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="num"
                          type="number"
                          value={l.gross_weight_kg}
                          onChange={(e) =>
                            setLine(i, { gross_weight_kg: e.target.value })
                          }
                        />
                      </td>
                      <td className="num">
                        {Math.round(amount * 100) / 100} {currency}
                      </td>
                      <td>
                        <button
                          className="link-btn"
                          onClick={() =>
                            setLines((ls) => ls.filter((_, idx) => idx !== i))
                          }
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button
              className="secondary"
              onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])}
            >
              + 라인 추가
            </button>

            {error && (
              <p className="notice-error" role="alert">
                {error}
              </p>
            )}

            <div className="form-actions">
              <button
                onClick={save}
                disabled={busy || !shipperId || !country}
              >
                {busy ? "저장 중…" : "딜 생성"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function PartnerSelect({
  partners,
  value,
  onChange,
}: {
  partners: Partner[];
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">선택</option>
      {partners.map((p) => (
        <option key={p.id} value={p.id}>
          {p.company_name} ({p.country})
        </option>
      ))}
    </select>
  );
}
